import React, { createContext, useContext, useState, ReactNode } from "react";

export type ModalContent =
    | undefined
    | "leadForm"
    | "terms"
    | "privacy"
    | "listingPage"
    | "categoryPage"
    | "disclaimer"
    | "disclaimer2"
    | "homePage"
    | "ccpaPrivacyNotice"
    | "sideWidget"
    | "compareCovered";

interface ListingState {
    questions: { [key: string]: any };
    loading: boolean;
}

interface ModalState {
    active: boolean;
    content: ModalContent;
    listing: ListingState;
}

interface ModalContextType {
    modal: ModalState;
    setModal: (state: Partial<ModalState>) => void;
    closeModal: () => void;
    setListingLoading: (loading: boolean) => void;
    setQuestionValue: (payload: { name: string; value: string }) => void;
    initiateQuestions: (questions: { [key: string]: any }) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
    const [modal, setModalState] = useState<ModalState>({
        active: false,
        content: undefined,
        listing: {
            questions: {},
            loading: false,
        },
    });

    const setModal = (state: Partial<ModalState>) => {
        setModalState((prev) => ({ ...prev, ...state }));
    };

    const closeModal = () => {
        setModalState((prev) => ({
            ...prev,
            active: false,
            content: undefined,
        }));
    };

    const setListingLoading = (loading: boolean) => {
        setModalState((prev) => ({
            ...prev,
            listing: { ...prev.listing, loading },
        }));
    };

    const setQuestionValue = (payload: { name: string; value: string }) => {
        setModalState((prev) => ({
            ...prev,
            listing: {
                ...prev.listing,
                questions: {
                    ...prev.listing.questions,
                    [payload.name]: {
                        ...((prev.listing.questions[payload.name] as
                            | Record<string, any>
                            | undefined) ?? {}),
                        value: payload.value,
                    },
                },
            },
        }));
    };

    const initiateQuestions = (questions: { [key: string]: any }) => {
        setModalState((prev) => ({
            ...prev,
            listing: { ...prev.listing, questions },
        }));
    };

    return (
        <ModalContext.Provider
            value={{
                modal,
                setModal,
                closeModal,
                setListingLoading,
                setQuestionValue,
                initiateQuestions,
            }}
        >
            {children}
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (context === undefined) {
        throw new Error("useModal must be used within a ModalProvider");
    }
    return context;
};
